import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';

type NullableDateString = string | null | undefined;

export interface HotelDailyAmount {
  daily_amount: number;
  total_daily_amount: number;
}

interface UseHotelDailyAmountParams {
  tripId?: number;
  checkInDate?: NullableDateString;
  checkOutDate?: NullableDateString;
  enabled?: boolean;
}

export function useHotelDailyAmount({
  tripId,
  checkInDate,
  checkOutDate,
  enabled = true,
}: UseHotelDailyAmountParams) {
  return useQuery<HotelDailyAmount, Error>({
    queryKey: [
      'hotel-daily-amount-request',
      tripId ?? null,
      checkInDate ?? null,
      checkOutDate ?? null,
    ],
    enabled: Boolean(enabled && tripId && checkInDate && checkOutDate),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!tripId || !checkInDate || !checkOutDate) {
        throw new Error('Missing hotel amount request parameters');
      }

      const response = await apiFetch('/api/v1/business-trip-hotels/daily-amount-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_trip_id: tripId,
          check_in_date: checkInDate,
          check_out_date: checkOutDate,
        }),
      });

      const payload = (await response.json().catch(() => null)) as
        | HotelDailyAmount
        | { error?: { message?: string } }
        | null;

      if (
        !response.ok ||
        !payload ||
        typeof payload !== 'object' ||
        !('daily_amount' in payload && 'total_daily_amount' in payload)
      ) {
        const message =
          payload &&
          typeof payload === 'object' &&
          'error' in payload &&
          payload.error &&
          typeof payload.error.message === 'string'
            ? payload.error.message
            : 'Failed to fetch hotel daily amount';
        throw new Error(message);
      }

      return payload as HotelDailyAmount;
    },
  });
}
